import type { AxiosResponse } from 'axios'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { fetchCompanyMembers } from '@services/auth/api'
import { callUrl } from '@services/common/api'

import { getActiveCompanyId, getErrorMessage, getFormErrors } from '@helpers'

import type { CompanyConsentsResponse, CompanyConsentTypes } from '@contexts/CompanyConsentProvider'

import type { EventsLogResult } from '@components/GenericEventsLogContainer/types'

import type { TagsDataTypes } from '@constants'

import expenseActions from '../expense/actions'
import incomeActions from '../income/actions'
import quarantineActions from '../quarantine/actions'
import type { CommonAxiosPayload } from '../types'
import actions from './actions'
import * as api from './api'
import type {
  MonthlyTaxCreateFormValues,
  MonthlyTaxUpdateFormValues,
  StatusCheckResponse,
  TaxGroupDetailsResponse,
} from './types'

function* initializeSaga({ meta: { resolve, reject } }: AsyncSagaAction) {
  try {
    const response: AxiosResponse<BackendInitResponse> = yield call(api.initialize)
    yield put(actions.initialize.success(response.data))
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.initialize.failure())
    yield call(reject, errorMsg)
  }
}

// status check
function* dashboardStatusCheckSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<StatusCheckResponse> = yield call(api.dashboardStatusCheck, companyId)
    yield put(actions.dashboardStatusCheck.success(response.data))
  } catch (_error) {
    // const errorMsg = getErrorMessage(error)
    yield put(actions.dashboardStatusCheck.failure())
  }
}

// TAGS
function* fetchTagsSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<Tag[]> = yield call(api.fetchTags, companyId)
    yield put(actions.fetchTags.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchTags.failure(errorMsg))
  }
}

function* createTagSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<{ name: string; company: number }>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<Tag> = yield call(api.createTag, companyId, payload)
    yield put(actions.createTag.success(response.data))
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// TAX
// called by salary editor - do not update store
function* fetchTaxGroupDetailsToStateSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<number>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<TaxGroupDetailsResponse> = yield call(api.fetchTaxGroupDetails, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
    console.error(error)
  }
}

// user viewed new tax
function* userViewTaxV2Saga({ payload }: SagaAction<number>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    yield call(api.userViewTax, companyId, payload)
    yield put(actions.userViewTaxV2.success(payload))
  } catch (_error) {
    // do nothing
  }
}

// manual create monthly tax
function* createMonthlyTaxV2Saga({ payload, meta: { resolve, reject } }: AsyncSagaAction<MonthlyTaxCreateFormValues>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<TaxGroupDetailsResponse> = yield call(api.createMonthlyTax, companyId, payload)
    yield put(actions.createMonthlyTaxV2.success(response.data))
    yield call(resolve, response.data)
  } catch (error) {
    const formErrors = getFormErrors(error)
    yield call(reject, formErrors)
  }
}

function* updateMonthlyTaxV2Saga({ payload, meta: { resolve, reject } }: AsyncSagaAction<MonthlyTaxUpdateFormValues>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<TaxGroupDetailsResponse> = yield call(api.updateMonthlyTax, companyId, payload)
    yield put(actions.updateMonthlyTaxV2.success(response.data))
    yield call(resolve, response.data)
  } catch (error) {
    const formErrors = getFormErrors(error)
    yield call(reject, formErrors)
  }
}

function* removeMonthlyTaxV2Saga({ payload, meta: { resolve, reject } }: AsyncSagaAction<{ id: ItemIdType }>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    yield call(api.removeMonthlyTax, companyId, payload.id)
    yield put(actions.removeMonthlyTaxV2.success(payload))
    yield call(resolve)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// TAX CODES
function* fetchTaxCodesSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<TaxCode[]> = yield call(api.fetchTaxCodes, companyId)
    yield put(actions.fetchTaxCodes.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchTaxCodes.failure(errorMsg))
  }
}

// BULK TAGGING
function* initTaggingSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<{ data_type: TagsDataTypes; ids: ItemIdType[] }>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<TaggingInitResultData> = yield call(api.initTagging, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

//* ACCOUNTING (Yeri)
function* fetchAssignmentNoteRecommendationsSaga({ meta: { resolve, reject } }: AsyncSagaAction) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<string[]> = yield call(api.fetchAssignmentNoteRecommendations, companyId)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* fetchLedgerNumbersSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<LedgerNumber[]> = yield call(api.fetchLedgerNumbers, companyId)
    yield put(actions.fetchLedgerNumbers.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    // do nothing
    console.error('Fetch ledger numbers failed:', errorMsg)
  }
}

function* createLedgerNumberSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<{ code: string; name: string }>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<LedgerNumber> = yield call(api.createLedgerNumber, companyId, payload)
    yield put(actions.createLedgerNumber.success(response.data))
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// v2 transactions warning notification
function* refreshTransactionsPageSaga({ payload }: SagaAction<'expense' | 'quarantine' | 'income'>) {
  if (payload === 'expense') {
    yield put(expenseActions.triggerExpenseListUpdate.request())
  }
  if (payload === 'quarantine') {
    yield put(quarantineActions.triggerQuarantineListUpdate.request())
  }
  if (payload === 'income') {
    yield put(incomeActions.triggerIncomeListUpdate.request())
  }
}

//* v2 event log (generic)
function* fetchEventsLogDataSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<{
  documentId: number
  documentType: 'document' | 'expense' | 'income'
}>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<EventsLogResult> = yield call(api.fetchEventsLogData, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* getCompanyConsentsSaga({ meta: { resolve, reject } }: AsyncSagaAction) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<CompanyConsentsResponse> = yield call(api.getCompanyConsents, companyId)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* setCompanyConsentSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<CompanyConsentTypes>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<CompanyConsentsResponse> = yield call(api.setCompanyConsent, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* fetchApproverUsersSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<{ users: CompanyUser[] }> = yield call(fetchCompanyMembers, companyId)
    yield put(actions.fetchApproverUsers.success(response.data.users))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    // do nothing
    console.error('Fetch approver users failed:', errorMsg)
  }
}

//* OAuth
function* initOAuthSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<OAuthParams>) {
  try {
    const response: AxiosResponse<OAuthInitResponse | OAuthRedirectResponse> = yield call(api.initOAuth, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* authorizeOAuthSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<CommonAxiosPayload<{ allow: boolean }>>) {
  try {
    const response: AxiosResponse<OAuthRedirectResponse> = yield call(callUrl, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// watcher Saga
export default function* commonSaga() {
  yield takeLatest(actions.initialize.REQUEST, initializeSaga)
  // expense status check
  yield takeLatest(actions.dashboardStatusCheck.REQUEST, dashboardStatusCheckSaga)
  // tags
  yield takeLatest(actions.fetchTags.REQUEST, fetchTagsSaga)
  yield takeLatest(actions.createTag.REQUEST, createTagSaga)

  // tax
  yield takeLatest(actions.fetchTaxGroupDetailsToState.REQUEST, fetchTaxGroupDetailsToStateSaga)
  // tax code
  yield takeLatest(actions.fetchTaxCodes.REQUEST, fetchTaxCodesSaga)
  // tagging
  yield takeLatest(actions.initTagging.REQUEST, initTaggingSaga)
  //*v2
  yield takeLatest(actions.createMonthlyTaxV2.REQUEST, createMonthlyTaxV2Saga)
  yield takeLatest(actions.userViewTaxV2.REQUEST, userViewTaxV2Saga)
  yield takeLatest(actions.updateMonthlyTaxV2.REQUEST, updateMonthlyTaxV2Saga)
  yield takeLatest(actions.removeMonthlyTaxV2.REQUEST, removeMonthlyTaxV2Saga)
  //* accounting
  yield takeLatest(actions.fetchAssignmentNoteRecommendations.REQUEST, fetchAssignmentNoteRecommendationsSaga)
  yield takeLatest(actions.fetchLedgerNumbers.REQUEST, fetchLedgerNumbersSaga)
  yield takeLatest(actions.createLedgerNumber.REQUEST, createLedgerNumberSaga)
  yield takeLatest(actions.refreshTransactionsPage.REQUEST, refreshTransactionsPageSaga)
  // events log v2
  yield takeLatest(actions.fetchEventsLogData.REQUEST, fetchEventsLogDataSaga)
  //* company consents
  yield takeLatest(actions.getCompanyConsents.REQUEST, getCompanyConsentsSaga)
  yield takeLatest(actions.setCompanyConsent.REQUEST, setCompanyConsentSaga)
  yield takeLatest(actions.fetchApproverUsers.REQUEST, fetchApproverUsersSaga)
  //* 0auth
  yield takeLatest(actions.initOAuth.REQUEST, initOAuthSaga)
  yield takeLatest(actions.callOAuthAuthorizeUrl.REQUEST, authorizeOAuthSaga)
}
